/**
 * RecordingSettings — Full-screen centred modal with all recording settings.
 *
 * Sections: Aspect Ratio, Camera, Microphone, Mouse Cursor Effect.
 * Corner Radius uses a sensible default (16px) — no user-facing slider.
 * Canvas Padding removed (not useful for MVP).
 */

import React, { useEffect, useState } from "react";

import { getAudioDevices } from "../data/recordingService";
import { useAppI18n } from "../hooks/useAppI18n";

import type {
  RecordingSettings as SettingsType,
  AspectRatio,
} from "../hooks/useRecordingState";

// ─── Constants ────────────────────────────────────────────────────────────────

const ASPECT_RATIO_VALUES: {
  value: AspectRatio;
  label: string;
  subKey: string;
}[] = [
  { value: "16:9", label: "16:9", subKey: "recording.ratioYouTube" },
  { value: "4:3", label: "4:3", subKey: "recording.ratioClassic" },
  { value: "3:4", label: "3:4", subKey: "recording.ratioRedNote" },
  { value: "9:16", label: "9:16", subKey: "recording.ratioTikTok" },
  { value: "1:1", label: "1:1", subKey: "recording.ratioSquare" },
  { value: "custom", label: "", subKey: "recording.ratioCustomSub" },
];

const CAMERA_SIZE_VALUES: { value: number; labelKey: string }[] = [
  { value: 100, labelKey: "recording.sizeSmall" },
  { value: 150, labelKey: "recording.sizeMedium" },
  { value: 200, labelKey: "recording.sizeLarge" },
  { value: 280, labelKey: "recording.sizeXL" },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface RecordingSettingsProps {
  isOpen: boolean;
  settings: SettingsType;
  onUpdate: (partial: Partial<SettingsType>) => void;
  onClose: () => void;
}

export const RecordingSettings: React.FC<RecordingSettingsProps> = ({
  isOpen,
  settings,
  onUpdate,
  onClose,
}) => {
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const { t } = useAppI18n();

  useEffect(() => {
    if (isOpen) {
      getAudioDevices().then(setAudioDevices);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="recording-settings-overlay" onClick={onClose}>
      <div className="recording-settings" onClick={(e) => e.stopPropagation()}>
        {/* Header row: title + close */}
        <div className="recording-settings__header">
          <h2 className="recording-settings__title">
            {t("recording.settingsTitle")}
          </h2>
          <button
            type="button"
            className="recording-settings__close"
            onClick={onClose}
            aria-label={t("recording.close")}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="recording-settings__divider" />

        {/* ── Aspect Ratio ─────────────────────────────────────────────── */}
        <section className="recording-settings__section">
          <label className="recording-settings__label">
            {t("recording.aspectRatio")}
          </label>
          <div className="recording-settings__ratio-grid">
            {ASPECT_RATIO_VALUES.map((ar) => (
              <button
                key={ar.value}
                type="button"
                className={`recording-settings__ratio-btn ${
                  settings.aspectRatio === ar.value
                    ? "recording-settings__ratio-btn--active"
                    : ""
                }`}
                onClick={() => onUpdate({ aspectRatio: ar.value })}
              >
                <span className="recording-settings__ratio-label">
                  {ar.value === "custom"
                    ? t("recording.ratioCustom")
                    : ar.label}
                </span>
                <span className="recording-settings__ratio-sub">
                  {t(ar.subKey)}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* ── Camera ───────────────────────────────────────────────────── */}
        <section className="recording-settings__section">
          <label className="recording-settings__label">
            {t("recording.camera")}
          </label>
          <div className="recording-settings__toggle-row">
            <button
              type="button"
              className={`recording-settings__toggle ${
                settings.showCamera ? "recording-settings__toggle--on" : ""
              }`}
              onClick={() => onUpdate({ showCamera: !settings.showCamera })}
              role="switch"
              aria-checked={settings.showCamera}
            >
              <span className="recording-settings__toggle-thumb" />
            </button>
            <span className="recording-settings__toggle-text">
              {t("recording.showCameraBubble")}
            </span>
          </div>

          <div className="recording-settings__size-row">
            <span className="recording-settings__size-label">
              {t("recording.bubbleSize")}
            </span>
            <div className="recording-settings__size-btns">
              {CAMERA_SIZE_VALUES.map((cs) => (
                <button
                  key={cs.value}
                  type="button"
                  className={`recording-settings__size-btn ${
                    settings.cameraSize === cs.value
                      ? "recording-settings__size-btn--active"
                      : ""
                  }`}
                  onClick={() => onUpdate({ cameraSize: cs.value })}
                  disabled={!settings.showCamera}
                >
                  {t(cs.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── Microphone ───────────────────────────────────────────────── */}
        <section className="recording-settings__section">
          <label className="recording-settings__label">
            {t("recording.microphone")}
          </label>
          <div className="recording-settings__select-wrap">
            <select
              className="recording-settings__select"
              value={settings.microphone}
              onChange={(e) => onUpdate({ microphone: e.target.value })}
            >
              <option value="none">{t("recording.noMicrophone")}</option>
              <option value="default">{t("recording.systemDefault")}</option>
              {audioDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
            <span className="material-symbols-outlined recording-settings__select-arrow">
              expand_more
            </span>
          </div>
        </section>

        {/* ── Done button ──────────────────────────────────────────────── */}
        <button
          type="button"
          className="recording-settings__done-btn"
          onClick={onClose}
        >
          {t("recording.done")}
        </button>
      </div>
    </div>
  );
};
